import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import Dialog, { DialogProps } from '../modals/Dialog'
import queryKeys from '../../services/queryKeys'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import { useLoanContext } from '../../contexts/LoanContext'
import { Loading } from '../Loading'
import { Button } from '../Button'
import LayoutRow from '../layout/LayoutRow'
import { useMediaLibrary } from './MediaLibraryContext'
import { MediaFileProps } from './types'
import AlertBox from '../AlertBox'

export type MediaLibraryPreviewModalProps = {
	media: MediaFileProps
} & DialogProps

const MediaLibraryPreviewModal = ({
	media,
	onClose,
	open,
}: MediaLibraryPreviewModalProps) => {
	const { loanId } = useLoanContext()
	const { formatMessage } = useIntl()
	const { selectActions } = useMediaLibrary()

	const { data: base64Data, isFetching: isFetchingBase64Data } =
		useQuery({
			queryKey: [queryKeys.loanDocumentContent, media?.id ?? ''],
			queryFn: () =>
				TheBigPOSApi.getLoanDocumentContent(loanId, media?.id ?? '', {
					contentType: 'base64',
				}),
			enabled:
				!!loanId &&
				!!media?.id &&
				!media?.base64Src &&
				!media?.processing,
			select: (res) => res.data,
		})

	if (!media) return null

	const { id, fileName, name, extension, createdAt } = media

	const base64Src =
		media.base64Src ??
		(base64Data
			? `data:application/${extension};base64,${base64Data}`
			: undefined)

	const image = media.url ?? base64Src ?? false

	const handleClose = () => {
		onClose?.()
	}

	return (
		<Dialog
			title={name}
			open={!!media && open}
			onClose={handleClose}
			className="max-w-screen-xl mx-auto my-10"
			fullScreen
			PaperProps={{
				className: '!max-h-200',
			}}
		>
			<div className="flex flex-col md:grid grid-cols-3 gap-6">
				<div className="col-span-2">
					<div className="relative pt-[95%] md:pt-[680px] bg-gray-200">
						{image && !isFetchingBase64Data && (
							<div className="absolute top-0 left-0 w-full h-full flex items-center justify-center p-6">
								<img
									src={image}
									alt={name}
									className="object-contain h-auto max-h-full"
								/>
							</div>
						)}
						{isFetchingBase64Data && (
							<div className="absolute top-0 left-0 w-full h-full object-contain flex justify-center items-center ">
								<Loading size="small" />
							</div>
						)}
					</div>
				</div>
				<div>
					<LayoutRow>
						<p>
							{formatMessage({ id: 'global.file_id' })}:{' '}
							<strong>{id}</strong>
						</p>
						<p>
							{formatMessage({ id: 'global.file_name' })}:{' '}
							<strong>{fileName}</strong>
						</p>
						<p>
							{formatMessage({ id: 'global.name' })}:{' '}
							<strong>{name}</strong>
						</p>
						<p>
							{formatMessage({ id: 'global.created_at' })}:{' '}
							<strong>{createdAt}</strong>
						</p>
					</LayoutRow>
					{media.processing && (
						<AlertBox severity="info">
							<p>
								{formatMessage({
									id: 'media_library.file_processing',
								})}
							</p>
						</AlertBox>
					)}
					{!media.processing &&
						selectActions &&
						selectActions.length > 0 && (
							<LayoutRow className="flex flex-wrap gap-2">
								{selectActions.map((action) => (
									<Button
										key={action.text}
										{...action}
										onClick={() => action.onClick(media)}
									/>
								))}
							</LayoutRow>
						)}
				</div>
			</div>
		</Dialog>
	)
}

export default MediaLibraryPreviewModal
